import _, { trim, isNumber, uniqBy, uniq, isObjectLike, intersection, range, isNull, isUndefined, get } from 'lodash';
import pino from 'pino';

/**
 * Used to match URLs that can appear anywhere in a string, not perfect, but crafting a perfect
 * URL regex is quite complex and we only need this for legacy comments
 */
const MID_STRING_URL_RGX = /https?:\/\/\S+/gi;
/**
 * Check whether a doc is empty
 */
function isDocEmpty(doc) {
    if (!doc)
        return true;
    return trim(documentToBasicString(doc, 1)).length < 1;
}
/**
 * Convert document to a basic string without all of the formatting, HTML tags, attributes etc.
 * Useful for previews or text analysis.
 * @param stopAtLength If set, will stop further parsing when the resulting string length
 * reaches this length. Useful when you're only interested in the first few characters of the document.
 */
function documentToBasicString(doc, stopAtLength = undefined) {
    if (!doc)
        return '';
    const recursiveStringBuilder = (doc, currentString) => {
        var _a;
        if (isNumber(stopAtLength) && currentString.length >= stopAtLength) {
            return currentString;
        }
        if (doc.text) {
            currentString += doc.text;
        }
        // if mention, add it as text as well
        if (doc.type === 'mention' && ((_a = doc.attrs) === null || _a === void 0 ? void 0 : _a.label) && doc.attrs.id) {
            currentString += '@' + doc.attrs.label;
        }
        for (const contentDoc of doc.content || []) {
            currentString = recursiveStringBuilder(contentDoc, currentString);
        }
        return currentString;
    };
    return recursiveStringBuilder(doc, '');
}
/**
 * Build a rich text document out of a basic string
 */
function convertBasicStringToDocument(text) {
    // Extract URLs and convert to text with link marks
    const urlMatches = [...text.matchAll(MID_STRING_URL_RGX)].map((m) => m[0]);
    const splitTexts = text.split(MID_STRING_URL_RGX);
    const textNodes = [];
    for (const textPart of splitTexts) {
        // Build text node, if text part not empty
        if (trim(textPart)) {
            textNodes.push({
                type: 'text',
                text: textPart
            });
        }
        // Get url node to append, if any remaining
        const url = urlMatches.shift();
        if (url) {
            textNodes.push({
                type: 'text',
                text: url,
                marks: [
                    {
                        attrs: {
                            href: url,
                            target: '_blank'
                        },
                        type: 'link'
                    }
                ]
            });
        }
    }
    return {
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                content: textNodes
            }
        ]
    };
}

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isDocEmpty: isDocEmpty,
  documentToBasicString: documentToBasicString,
  convertBasicStringToDocument: convertBasicStringToDocument
});

const clefLevels = {
    60: 'Fatal',
    50: 'Error',
    40: 'Warning',
    30: 'Information',
    20: 'Debug',
    10: 'Verbose' // TRACE / Verbose
};
function toClef(log) {
    // add the stuff CLEF wants ....
    if (log.time) {
        log['@t'] = new Date(log.time).toISOString();
    }
    else {
        log['@t'] = new Date().toISOString();
    }
    if (log.level && log.level in clefLevels) {
        log['@l'] = clefLevels[log.level];
    }
    const exception = log.error || log.err;
    if (exception) {
        // Seq wants it as a string - try to prettify it with a legible stack trace
        log['@x'] = exception.stack;
    }
    return log;
}

let logger;
function getLogger(logLevel = 'info', pretty = false, mixin) {
    if (logger)
        return logger;
    const pinoOptions = {
        base: undefined,
        formatters: {
            level: (label, number) => 
            // for not pretty, we're providing clef levels
            pretty
                ? { level: label }
                : {
                    '@l': number in clefLevels
                        ? clefLevels[number]
                        : clefLevels[30]
                },
            log: (logObject) => (pretty ? logObject : toClef(logObject))
        },
        mixin,
        // when not pretty, to produce a clef format, we need the message to be the message template key
        messageKey: pretty ? 'msg' : '@mt',
        level: logLevel,
        // when not pretty, we need the time in the clef appropriate field, not from pino
        timestamp: pretty ? pino.stdTimeFunctions.isoTime : false
    };
    if (pretty) {
        pinoOptions.transport = {
            target: '@speckle/shared/pinoPrettyTransport.js',
            options: {
                colorize: true,
                destination: 2,
                ignore: 'time',
                levelFirst: true,
                singleLine: true
            }
        };
    }
    logger = pino(pinoOptions);
    return logger;
}
function extendLoggerComponent(otherChild, ...subComponent) {
    const otherChildBindings = otherChild.bindings();
    otherChildBindings.component = [otherChildBindings.component, ...subComponent]
        .filter(Boolean)
        .join('/');
    return otherChild.child(otherChildBindings);
}
/**
 * Very simple RPM counter to catch extreme spam scenarios (e.g. a ton of errors being thrown). It's not going
 * to always report accurately, but as long as hits are being registered consistently it should be accurate enough.
 */
function simpleRpmCounter() {
    const getTimestamp = () => new Date().getTime();
    let lastDateTimestamp = getTimestamp();
    let hits = 0;
    const validateHits = () => {
        const timestamp = getTimestamp();
        if (timestamp > lastDateTimestamp + 60 * 1000) {
            hits = 0;
            lastDateTimestamp = timestamp;
        }
    };
    return {
        hit: () => {
            validateHits();
            return ++hits;
        },
        get: () => {
            validateHits();
            return hits;
        }
    };
}

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getLogger: getLogger,
  extendLoggerComponent: extendLoggerComponent,
  simpleRpmCounter: simpleRpmCounter
});

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

var _ViewerResourceBuilder_resources;
var ViewerResourceType;
(function (ViewerResourceType) {
    ViewerResourceType["Model"] = "Model";
    ViewerResourceType["Object"] = "Object";
    ViewerResourceType["ModelFolder"] = "ModelFolder";
    ViewerResourceType["AllModels"] = "all-models";
})(ViewerResourceType || (ViewerResourceType = {}));
class ViewerAllModelsResource {
    constructor() {
        this.type = ViewerResourceType.AllModels;
    }
    toString() {
        return 'all';
    }
}
class ViewerModelResource {
    constructor(modelId, versionId) {
        this.type = ViewerResourceType.Model;
        this.modelId = modelId;
        this.versionId = versionId;
    }
    toString() {
        return (this.versionId ? `${this.modelId}@${this.versionId}` : this.modelId).toLowerCase();
    }
}
class ViewerVersionResource extends ViewerModelResource {
    constructor(modelId, versionId) {
        super(modelId, versionId);
        this.versionId = versionId;
    }
    toJSON() {
        return this.toString();
    }
}
class ViewerObjectResource {
    constructor(objectId) {
        this.type = ViewerResourceType.Object;
        this.objectId = objectId;
    }
    toString() {
        return this.objectId.toLowerCase();
    }
}
class ViewerModelFolderResource {
    constructor(folderName) {
        this.type = ViewerResourceType.ModelFolder;
        this.folderName = folderName;
    }
    toString() {
        return ('$' + this.folderName).toLowerCase();
    }
}
function parseUrlParameters(resourceGetParam) {
    if (!(resourceGetParam === null || resourceGetParam === void 0 ? void 0 : resourceGetParam.length))
        return [];
    const parts = resourceGetParam.toLowerCase().split(',').sort();
    const resources = [];
    for (const part of parts) {
        if (part === 'all') {
            resources.push(new ViewerAllModelsResource());
        }
        else if (part.includes('@')) {
            const [modelId, versionId] = part.split('@');
            resources.push(new ViewerModelResource(modelId, versionId));
        }
        else if (part.startsWith('$')) {
            resources.push(new ViewerModelFolderResource(part.substring(1)));
        }
        else if (part.length === 32) {
            resources.push(new ViewerObjectResource(part));
        }
        else {
            resources.push(new ViewerModelResource(part));
        }
    }
    // Remove duplicates
    return uniqBy(resources, (r) => r.toString());
}
function createGetParamFromResources(resources) {
    const resourceParts = uniq(resources.map((r) => r.toString().toLowerCase())).sort();
    return resourceParts.join(',');
}
const isAllModelsResource = (r) => r.type === ViewerResourceType.AllModels;
const isModelResource = (r) => r.type === ViewerResourceType.Model;
const isObjectResource = (r) => r.type === ViewerResourceType.Object;
const isModelFolderResource = (r) => r.type === ViewerResourceType.ModelFolder;
class ViewerResourceBuilder {
    constructor() {
        _ViewerResourceBuilder_resources.set(this, []);
    }
    addAllModels() {
        __classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f").push(new ViewerAllModelsResource());
        return this;
    }
    addModel(modelId, versionId) {
        __classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f").push(new ViewerModelResource(modelId, versionId));
        return this;
    }
    addModelFolder(folderName) {
        __classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f").push(new ViewerModelFolderResource(folderName));
        return this;
    }
    addObject(objectId) {
        __classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f").push(new ViewerObjectResource(objectId));
        return this;
    }
    toString() {
        return createGetParamFromResources(__classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f"));
    }
    toResources() {
        return __classPrivateFieldGet(this, _ViewerResourceBuilder_resources, "f").slice();
    }
    clear() {
        __classPrivateFieldSet(this, _ViewerResourceBuilder_resources, [], "f");
        return this;
    }
}
_ViewerResourceBuilder_resources = new WeakMap();
/**
 * Fluent API for easier resource building
 */
function resourceBuilder() {
    return new ViewerResourceBuilder();
}

var route = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get ViewerResourceType () { return ViewerResourceType; },
  ViewerAllModelsResource: ViewerAllModelsResource,
  ViewerModelResource: ViewerModelResource,
  ViewerVersionResource: ViewerVersionResource,
  ViewerObjectResource: ViewerObjectResource,
  ViewerModelFolderResource: ViewerModelFolderResource,
  parseUrlParameters: parseUrlParameters,
  createGetParamFromResources: createGetParamFromResources,
  isAllModelsResource: isAllModelsResource,
  isModelResource: isModelResource,
  isObjectResource: isObjectResource,
  isModelFolderResource: isModelFolderResource,
  resourceBuilder: resourceBuilder
});

class UnformattableSerializedViewerStateError extends Error {
}

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  UnformattableSerializedViewerStateError: UnformattableSerializedViewerStateError
});

var MeasurementType;
(function (MeasurementType) {
    MeasurementType[MeasurementType["PERPENDICULAR"] = 0] = "PERPENDICULAR";
    MeasurementType[MeasurementType["POINTTOPOINT"] = 1] = "POINTTOPOINT";
})(MeasurementType || (MeasurementType = {}));
/**
 * v1 -> v1.1
 * - ui.filters.propertyFilter.isApplied field added
 * - ui.spotlightUserId swapped for spotlightUserSessionId
 * v1.1 -> v1.2
 * - ui.diff added
 */
const SERIALIZED_VIEWER_STATE_VERSION = 1.2;
/**
 * Note: This only does superficial validation. To really ensure that all of the keys are there, even if prefilled with default values, make sure you invoke
 * formatSerializedViewerState() on the state afterwards
 */
const isSerializedViewerState = (val) => {
    if (!val)
        return false;
    const keys = [
        'projectId',
        'sessionId',
        'resources',
        'ui',
        'viewer'
    ];
    if (!isObjectLike(val))
        return false;
    const valKeys = Object.keys(val);
    if (intersection(valKeys, keys).length !== keys.length)
        return false;
    return true;
};
const initializeMissingData = (state) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54;
    const throwInvalidError = (missingPath) => {
        throw new UnformattableSerializedViewerStateError('Required data missing from SerializedViewerState: ' + missingPath);
    };
    const defaultMeasurementOptions = {
        visible: false,
        type: MeasurementType.POINTTOPOINT,
        vertexSnap: false,
        units: 'm',
        precision: 2
    };
    const measurementOptions = {
        ...defaultMeasurementOptions,
        ...(_b = (_a = state.ui) === null || _a === void 0 ? void 0 : _a.measurement) === null || _b === void 0 ? void 0 : _b.options
    };
    return {
        projectId: state.projectId || throwInvalidError('projectId'),
        sessionId: state.sessionId || `nullSessionId-${Math.random() * 1000}`,
        viewer: {
            ...(state.viewer || {}),
            metadata: {
                ...(((_c = state.viewer) === null || _c === void 0 ? void 0 : _c.metadata) || {}),
                filteringState: ((_e = (_d = state.viewer) === null || _d === void 0 ? void 0 : _d.metadata) === null || _e === void 0 ? void 0 : _e.filteringState) || null
            }
        },
        resources: {
            ...(state.resources || {}),
            request: {
                ...(((_f = state.resources) === null || _f === void 0 ? void 0 : _f.request) || {}),
                resourceIdString: ((_h = (_g = state.resources) === null || _g === void 0 ? void 0 : _g.request) === null || _h === void 0 ? void 0 : _h.resourceIdString) ||
                    throwInvalidError('resources.request.resourceIdString'),
                threadFilters: {
                    ...(((_k = (_j = state.resources) === null || _j === void 0 ? void 0 : _j.request) === null || _k === void 0 ? void 0 : _k.threadFilters) || {}),
                    includeArchived: ((_o = (_m = (_l = state.resources) === null || _l === void 0 ? void 0 : _l.request) === null || _m === void 0 ? void 0 : _m.threadFilters) === null || _o === void 0 ? void 0 : _o.includeArchived) || false,
                    loadedVersionsOnly: ((_r = (_q = (_p = state.resources) === null || _p === void 0 ? void 0 : _p.request) === null || _q === void 0 ? void 0 : _q.threadFilters) === null || _r === void 0 ? void 0 : _r.loadedVersionsOnly) || false
                }
            }
        },
        ui: {
            ...(state.ui || {}),
            threads: {
                ...(((_s = state.ui) === null || _s === void 0 ? void 0 : _s.threads) || {}),
                openThread: {
                    ...(((_u = (_t = state.ui) === null || _t === void 0 ? void 0 : _t.threads) === null || _u === void 0 ? void 0 : _u.openThread) || {}),
                    threadId: ((_x = (_w = (_v = state.ui) === null || _v === void 0 ? void 0 : _v.threads) === null || _w === void 0 ? void 0 : _w.openThread) === null || _x === void 0 ? void 0 : _x.threadId) || null,
                    isTyping: ((_0 = (_z = (_y = state.ui) === null || _y === void 0 ? void 0 : _y.threads) === null || _z === void 0 ? void 0 : _z.openThread) === null || _0 === void 0 ? void 0 : _0.isTyping) || false,
                    newThreadEditor: ((_3 = (_2 = (_1 = state.ui) === null || _1 === void 0 ? void 0 : _1.threads) === null || _2 === void 0 ? void 0 : _2.openThread) === null || _3 === void 0 ? void 0 : _3.newThreadEditor) || false
                }
            },
            diff: {
                ...(((_4 = state.ui) === null || _4 === void 0 ? void 0 : _4.diff) || {}),
                command: ((_6 = (_5 = state.ui) === null || _5 === void 0 ? void 0 : _5.diff) === null || _6 === void 0 ? void 0 : _6.command) || null,
                time: ((_8 = (_7 = state.ui) === null || _7 === void 0 ? void 0 : _7.diff) === null || _8 === void 0 ? void 0 : _8.time) || 0.5,
                mode: ((_10 = (_9 = state.ui) === null || _9 === void 0 ? void 0 : _9.diff) === null || _10 === void 0 ? void 0 : _10.mode) || 1
            },
            spotlightUserSessionId: ((_11 = state.ui) === null || _11 === void 0 ? void 0 : _11.spotlightUserSessionId) || null,
            filters: {
                ...(((_12 = state.ui) === null || _12 === void 0 ? void 0 : _12.filters) || {}),
                isolatedObjectIds: ((_14 = (_13 = state.ui) === null || _13 === void 0 ? void 0 : _13.filters) === null || _14 === void 0 ? void 0 : _14.isolatedObjectIds) || [],
                hiddenObjectIds: ((_16 = (_15 = state.ui) === null || _15 === void 0 ? void 0 : _15.filters) === null || _16 === void 0 ? void 0 : _16.hiddenObjectIds) || [],
                selectedObjectIds: ((_18 = (_17 = state.ui) === null || _17 === void 0 ? void 0 : _17.filters) === null || _18 === void 0 ? void 0 : _18.selectedObjectIds) || [],
                propertyFilter: {
                    ...(((_20 = (_19 = state.ui) === null || _19 === void 0 ? void 0 : _19.filters) === null || _20 === void 0 ? void 0 : _20.propertyFilter) || {}),
                    key: ((_23 = (_22 = (_21 = state.ui) === null || _21 === void 0 ? void 0 : _21.filters) === null || _22 === void 0 ? void 0 : _22.propertyFilter) === null || _23 === void 0 ? void 0 : _23.key) || null,
                    isApplied: ((_26 = (_25 = (_24 = state.ui) === null || _24 === void 0 ? void 0 : _24.filters) === null || _25 === void 0 ? void 0 : _25.propertyFilter) === null || _26 === void 0 ? void 0 : _26.isApplied) || false
                }
            },
            camera: {
                ...(((_27 = state.ui) === null || _27 === void 0 ? void 0 : _27.camera) || {}),
                position: ((_29 = (_28 = state.ui) === null || _28 === void 0 ? void 0 : _28.camera) === null || _29 === void 0 ? void 0 : _29.position) || throwInvalidError('ui.camera.position'),
                target: ((_31 = (_30 = state.ui) === null || _30 === void 0 ? void 0 : _30.camera) === null || _31 === void 0 ? void 0 : _31.target) || throwInvalidError('ui.camera.target'),
                isOrthoProjection: ((_33 = (_32 = state.ui) === null || _32 === void 0 ? void 0 : _32.camera) === null || _33 === void 0 ? void 0 : _33.isOrthoProjection) || false,
                zoom: ((_35 = (_34 = state.ui) === null || _34 === void 0 ? void 0 : _34.camera) === null || _35 === void 0 ? void 0 : _35.zoom) || 1
            },
            sectionBox: ((_38 = (_37 = (_36 = state.ui) === null || _36 === void 0 ? void 0 : _36.sectionBox) === null || _37 === void 0 ? void 0 : _37.min) === null || _38 === void 0 ? void 0 : _38.length) && ((_40 = (_39 = state.ui) === null || _39 === void 0 ? void 0 : _39.sectionBox.max) === null || _40 === void 0 ? void 0 : _40.length)
                ? {
                    min: state.ui.sectionBox.min,
                    max: state.ui.sectionBox.max
                }
                : null,
            lightConfig: {
                ...(((_41 = state.ui) === null || _41 === void 0 ? void 0 : _41.lightConfig) || {}),
                intensity: (_43 = (_42 = state.ui) === null || _42 === void 0 ? void 0 : _42.lightConfig) === null || _43 === void 0 ? void 0 : _43.intensity,
                indirectLightIntensity: (_45 = (_44 = state.ui) === null || _44 === void 0 ? void 0 : _44.lightConfig) === null || _45 === void 0 ? void 0 : _45.indirectLightIntensity,
                elevation: (_47 = (_46 = state.ui) === null || _46 === void 0 ? void 0 : _46.lightConfig) === null || _47 === void 0 ? void 0 : _47.elevation,
                azimuth: (_49 = (_48 = state.ui) === null || _48 === void 0 ? void 0 : _48.lightConfig) === null || _49 === void 0 ? void 0 : _49.azimuth
            },
            explodeFactor: ((_50 = state.ui) === null || _50 === void 0 ? void 0 : _50.explodeFactor) || 0,
            selection: ((_51 = state.ui) === null || _51 === void 0 ? void 0 : _51.selection) || null,
            measurement: {
                enabled: (_54 = (_53 = (_52 = state.ui) === null || _52 === void 0 ? void 0 : _52.measurement) === null || _53 === void 0 ? void 0 : _53.enabled) !== null && _54 !== void 0 ? _54 : false,
                options: measurementOptions
            }
        }
    };
};
/**
 * Formats SerializedViewerState by bringing it up to date with the structure of the latest version
 * and ensuring missing keys are initialized with default values
 */
const formatSerializedViewerState = (state) => {
    const finalState = initializeMissingData(state);
    return finalState;
};

var state = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SERIALIZED_VIEWER_STATE_VERSION: SERIALIZED_VIEWER_STATE_VERSION,
  isSerializedViewerState: isSerializedViewerState,
  formatSerializedViewerState: formatSerializedViewerState
});

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ViewerRoute: route,
  ViewerState: state,
  ViewerErrors: index$1
});

/**
 * Speckle role constants
 * - Stream - user roles in the context of a specific stream
 * - Server - user roles in the context of the entire server
 */
const Roles = Object.freeze({
    Stream: {
        Owner: 'stream:owner',
        Contributor: 'stream:contributor',
        Reviewer: 'stream:reviewer'
    },
    Server: {
        Admin: 'server:admin',
        User: 'server:user',
        Guest: 'server:guest',
        ArchivedUser: 'server:archived-user'
    }
});
const RoleInfo = Object.freeze({
    Stream: {
        [Roles.Stream.Owner]: {
            title: 'Owner',
            description: 'Owners have full access, including deletion rights & access control.'
        },
        [Roles.Stream.Contributor]: {
            title: 'Contributor',
            description: 'Contributors can create new branches and commits, but they cannot edit stream details or manage collaborators.'
        },
        [Roles.Stream.Reviewer]: {
            title: 'Reviewer',
            description: 'Reviewers can only view (read) the data from this stream.'
        }
    },
    Server: {
        [Roles.Server.Admin]: 'Admin',
        [Roles.Server.User]: 'User',
        [Roles.Server.Guest]: 'Guest',
        [Roles.Server.ArchivedUser]: 'Archived'
    }
});
/**
 * Speckle scope constants
 * - Scopes define what kind of access has a user approved for a specific access token
 */
const Scopes = Object.freeze({
    Streams: {
        Read: 'streams:read',
        Write: 'streams:write'
    },
    Profile: {
        Read: 'profile:read',
        Email: 'profile:email',
        Delete: 'profile:delete'
    },
    Users: {
        Read: 'users:read',
        Email: 'users:email',
        Invite: 'users:invite'
    },
    Server: {
        Stats: 'server:stats',
        Setup: 'server:setup'
    },
    Tokens: {
        Read: 'tokens:read',
        Write: 'tokens:write'
    },
    Apps: {
        Read: 'apps:read',
        Write: 'apps:write'
    },
    Automate: {
        ReportResults: 'automate:report-results'
    }
});
/**
 * All scopes
 */
const AllScopes = _.flatMap(Scopes, (v) => Object.values(v));
const SourceAppNames = [
    'Dynamo',
    'Revit',
    'AutoCAD',
    'Civil3D',
    'Blender',
    'Rhino',
    'Grasshopper',
    'Excel',
    'Unity',
    'Unreal',
    'Python',
    '.NET',
    'IFC',
    'QGIS',
    'ArcGIS',
    'ETABS',
    'PowerBI',
    'SketchUp',
    'SAP2000',
    'CSiBridge',
    'SAFE',
    'Archicad',
    'Tekla Structures',
    'OpenRoads',
    'OpenRail',
    'OpenBuildings',
    'MicroStation',
    'Navisworks',
    'Speckle Automate'
];
const SourceApps = [
    { searchKey: 'dynamo', name: 'Dynamo', short: 'DYN', bgColor: '#a438b6' },
    { searchKey: 'revit', name: 'Revit', short: 'RVT', bgColor: '#3091e7' },
    { searchKey: 'autocad', name: 'AutoCAD', short: 'ACAD', bgColor: '#f0605e' },
    { searchKey: 'civil', name: 'Civil3D', short: 'C3D', bgColor: '#14c1d7' },
    { searchKey: 'blender', name: 'Blender', short: 'BLEND', bgColor: '#fb9514' },
    { searchKey: 'rhino', name: 'Rhino', short: 'RH', bgColor: '#141414' },
    { searchKey: 'grasshopper', name: 'Grasshopper', short: 'GH', bgColor: '#48974b' },
    { searchKey: 'excel', name: 'Excel', short: 'XLSX', bgColor: '#72c076' },
    { searchKey: 'unity', name: 'Unity', short: 'UNITY', bgColor: '#149e91' },
    { searchKey: 'unreal', name: 'Unreal', short: 'UE', bgColor: '#846256' },
    { searchKey: 'python', name: 'Python', short: 'PY', bgColor: '#fddb45' },
    { searchKey: '.net', name: '.NET', short: '.NET', bgColor: '#8531a9' },
    { searchKey: 'ifc', name: 'IFC', short: 'IFC', bgColor: '#bd2e2e' },
    { searchKey: 'qgis', name: 'QGIS', short: 'QGIS', bgColor: '#70e029' },
    { searchKey: 'arcgis', name: 'ArcGIS', short: 'AGIS', bgColor: '#3a6eff' },
    { searchKey: 'etabs', name: 'ETABS', short: 'ETABS', bgColor: '#6d6d6d' },
    { searchKey: 'powerbi', name: 'PowerBI', short: 'PBI', bgColor: '#ffff96' },
    { searchKey: 'sketchup', name: 'SketchUp', short: 'SKP', bgColor: '#8cb7ff' },
    { searchKey: 'sap', name: 'SAP2000', short: 'SAP', bgColor: '#6d6d6d' },
    { searchKey: 'csibridge', name: 'CSiBridge', short: 'CSIB', bgColor: '#6d6d6d' },
    { searchKey: 'safe', name: 'SAFE', short: 'SAFE', bgColor: '#6d6d6d' },
    { searchKey: 'archicad', name: 'Archicad', short: 'ARCHI', bgColor: '#3091e7' },
    {
        searchKey: 'teklastructures',
        name: 'Tekla Structures',
        short: 'TEKLAS',
        bgColor: '#3a6eff'
    },
    { searchKey: 'openroads', name: 'OpenRoads', short: 'OROAD', bgColor: '#846256' },
    { searchKey: 'openrail', name: 'OpenRail', short: 'ORAIL', bgColor: '#846256' },
    {
        searchKey: 'openbuildings',
        name: 'OpenBuildings',
        short: 'OBUILD',
        bgColor: '#846256'
    },
    {
        searchKey: 'microstation',
        name: 'MicroStation',
        short: 'MICRO',
        bgColor: '#846256'
    },
    { searchKey: 'navisworks', name: 'Navisworks', bgColor: '#3e8742', short: 'NAVIS' },
    {
        searchKey: 'automate',
        name: 'Speckle Automate',
        bgColor: '#f85c56',
        short: 'ATMAT'
    }
];
const WebhookTriggers = Object.freeze({
    StreamUpdate: 'stream_update',
    StreamDelete: 'stream_delete',
    BranchCreate: 'branch_create',
    BranchUpdate: 'branch_update',
    BranchDelete: 'branch_delete',
    CommitCreate: 'commit_create',
    CommitUpdate: 'commit_update',
    CommitReceive: 'commit_receive',
    CommitDelete: 'commit_delete',
    CommentCreated: 'comment_created',
    CommentArchived: 'comment_archived',
    CommentReplied: 'comment_replied',
    StreamPermissionsAdd: 'stream_permissions_add',
    StreamPermissionsRemove: 'stream_permissions_remove'
});

/**
 * Utility for batching async operations. Useful when you have thousands of async operations and you can't
 * just invoke them all at once because of resource constraints (e.g. network bandwidth).
 *
 * 'operationParams' should be an array of parameters for each async operation. The size of this array coresponds
 * to the amount of async operations that will be invoked.
 * 'operationPromiseGenerator' will be invoked sequentially with each params of 'operationParams' and with it
 * you can specify what the actual async operation should be
 *
 * TODO: Some tests would be nice, although it does work when tested through `yarn cli download commit` in speckle-server
 */
async function batchAsyncOperations(name, operationParams, operationPromiseGenerator, options) {
    const { retryCount = 3, batchSize = 100, dropReturns = false, logger = (...args) => console.log(...args) } = options || {};
    const finalLogger = (message, ...args) => logger(`[${name}] ${message}`, ...args);
    finalLogger('Starting batched operation...');
    const operationCount = operationParams.length;
    let allResults = [];
    let executedOperationCount = 0;
    const batchCount = Math.ceil(operationCount / batchSize);
    for (let i = 0; i < batchCount; i++) {
        finalLogger(`Processing batch ${i + 1} out of ${batchCount}...`);
        // Figure out how many operations we can execute in this batch
        const newExecutedOperationCount = Math.min(executedOperationCount + batchSize, operationCount);
        const operationsToExecuteCount = newExecutedOperationCount - executedOperationCount;
        if (operationsToExecuteCount <= 0)
            return;
        // Invoke operation generator
        const batchParams = operationParams.slice(executedOperationCount, newExecutedOperationCount);
        const batchRequests = [];
        let currentOperationIdx = executedOperationCount;
        for (const params of batchParams) {
            const currentOperationNumber = currentOperationIdx + 1;
            const label = `${currentOperationNumber}/${operationCount}`;
            const operationPromise = (async () => {
                finalLogger(`Queuing operation ${label}...`);
                const execute = () => operationPromiseGenerator(params);
                let promise = execute().then((res) => {
                    finalLogger(`...finished operation ${label}`);
                    return res;
                });
                // Attach retries
                range(retryCount).forEach((retry) => {
                    promise = promise.catch((e) => {
                        finalLogger(`...failure in operation ${label}: "${e}". Triggering retry ${retry + 1}...`);
                        return execute();
                    });
                });
                promise = promise.catch((e) => {
                    finalLogger(`...final failure in operation ${label}!`);
                    throw e;
                });
                return promise;
            })();
            batchRequests.push(operationPromise);
            currentOperationIdx++;
        }
        const batchResults = await Promise.all(batchRequests);
        if (!dropReturns) {
            allResults = allResults.concat(batchResults);
        }
        executedOperationCount = newExecutedOperationCount;
    }
    return dropReturns ? true : allResults;
}

class UnexpectedErrorStructureError extends Error {
}
/**
 * In JS catch clauses can receive not only Errors, but pretty much any other kind of data type, so
 * you can use this helper to ensure that whatever is passed in is a real error
 */
function ensureError(e, fallbackMessage) {
    if (e instanceof Error)
        return e;
    return new UnexpectedErrorStructureError(fallbackMessage);
}

/* Time with seconds as the base unit
 */
const TIME = {
    second: 1,
    minute: 60,
    hour: 60 * 60,
    day: 24 * 60 * 60,
    week: 7 * 24 * 60 * 60,
    month: 28 * 24 * 60 * 60
};

class TimeoutError extends Error {
}
class WaitIntervalUntilCanceledError extends Error {
}
/**
 * Build promise that can be resolved/rejected manually outside of the promise's execution scope
 */
const buildManualPromise = () => {
    let resolve;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    const resolveWrapper = (...args) => resolve(...args);
    const rejectWrapper = (...args) => reject(...args);
    return { promise, resolve: resolveWrapper, reject: rejectWrapper };
};
const isNullOrUndefined = (val) => isNull(val) || isUndefined(val);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const waitIntervalUntil = (ms, predicate) => {
    const { promise, resolve, reject } = buildManualPromise();
    const interval = setInterval(() => {
        if (predicate()) {
            clearInterval(interval);
            resolve();
        }
    }, ms);
    const ret = promise;
    ret.cancel = () => {
        clearInterval(interval);
        reject(new WaitIntervalUntilCanceledError());
    };
    return ret;
};
/**
 * Not nullable type guard, useful in `.filter()` calls for proper TS typed
 * results
 */
const isNonNullable = (v) => !!v;
/**
 * Make the promise throw after enough time has passed. Useful for implementing timeout functionality in various flows.
 */
const timeoutAt = (ms, optionalMessage) => new Promise((_resolve, reject) => setTimeout(() => {
    reject(new TimeoutError(optionalMessage || 'timeoutAt() timed out'));
}, ms));
/**
 * Invoke and return fn(), but retry it up to n times if it throws
 */
const retry = async (fn, n) => {
    let lastError;
    for (let i = 0; i < n; i++) {
        try {
            const res = await Promise.resolve(fn());
            return res;
        }
        catch (error) {
            lastError = ensureError(error);
        }
    }
    throw lastError;
};
/**
 * For quickly profiling a function
 */
const profile = async (fn, label, extra) => {
    const start = performance.now();
    const res = await Promise.resolve(fn());
    const end = performance.now();
    console.log(`[${label || 'profile'}] took ${end - start}ms`, ...(extra ? [extra] : []));
    return res;
};
/**
 * For quickly profiling a sync function
 */
const profileSync = (fn, label, extra) => {
    const start = performance.now();
    const res = fn();
    const end = performance.now();
    console.log(`[${label || 'profile'}] took ${end - start}ms`, ...(extra ? [extra] : []));
    return res;
};

/**
 * In TS undefined !== void, so use this type guard to check for both
 */
const isUndefinedOrVoid = (val) => isUndefined(val);

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Lightweight MD5 implementation.
 * @see http://www.myersdaily.org/joseph/javascript/md5-text.html
 */
function md5cycle(x, k) {
    let a = x[0], b = x[1], c = x[2], d = x[3];
    a = ff(a, b, c, d, k[0], 7, -680876936);
    d = ff(d, a, b, c, k[1], 12, -389564586);
    c = ff(c, d, a, b, k[2], 17, 606105819);
    b = ff(b, c, d, a, k[3], 22, -1044525330);
    a = ff(a, b, c, d, k[4], 7, -176418897);
    d = ff(d, a, b, c, k[5], 12, 1200080426);
    c = ff(c, d, a, b, k[6], 17, -1473231341);
    b = ff(b, c, d, a, k[7], 22, -45705983);
    a = ff(a, b, c, d, k[8], 7, 1770035416);
    d = ff(d, a, b, c, k[9], 12, -1958414417);
    c = ff(c, d, a, b, k[10], 17, -42063);
    b = ff(b, c, d, a, k[11], 22, -1990404162);
    a = ff(a, b, c, d, k[12], 7, 1804603682);
    d = ff(d, a, b, c, k[13], 12, -40341101);
    c = ff(c, d, a, b, k[14], 17, -1502002290);
    b = ff(b, c, d, a, k[15], 22, 1236535329);
    a = gg(a, b, c, d, k[1], 5, -165796510);
    d = gg(d, a, b, c, k[6], 9, -1069501632);
    c = gg(c, d, a, b, k[11], 14, 643717713);
    b = gg(b, c, d, a, k[0], 20, -373897302);
    a = gg(a, b, c, d, k[5], 5, -701558691);
    d = gg(d, a, b, c, k[10], 9, 38016083);
    c = gg(c, d, a, b, k[15], 14, -660478335);
    b = gg(b, c, d, a, k[4], 20, -405537848);
    a = gg(a, b, c, d, k[9], 5, 568446438);
    d = gg(d, a, b, c, k[14], 9, -1019803690);
    c = gg(c, d, a, b, k[3], 14, -187363961);
    b = gg(b, c, d, a, k[8], 20, 1163531501);
    a = gg(a, b, c, d, k[13], 5, -1444681467);
    d = gg(d, a, b, c, k[2], 9, -51403784);
    c = gg(c, d, a, b, k[7], 14, 1735328473);
    b = gg(b, c, d, a, k[12], 20, -1926607734);
    a = hh(a, b, c, d, k[5], 4, -378558);
    d = hh(d, a, b, c, k[8], 11, -2022574463);
    c = hh(c, d, a, b, k[11], 16, 1839030562);
    b = hh(b, c, d, a, k[14], 23, -35309556);
    a = hh(a, b, c, d, k[1], 4, -1530992060);
    d = hh(d, a, b, c, k[4], 11, 1272893353);
    c = hh(c, d, a, b, k[7], 16, -155497632);
    b = hh(b, c, d, a, k[10], 23, -1094730640);
    a = hh(a, b, c, d, k[13], 4, 681279174);
    d = hh(d, a, b, c, k[0], 11, -358537222);
    c = hh(c, d, a, b, k[3], 16, -722521979);
    b = hh(b, c, d, a, k[6], 23, 76029189);
    a = hh(a, b, c, d, k[9], 4, -640364487);
    d = hh(d, a, b, c, k[12], 11, -421815835);
    c = hh(c, d, a, b, k[15], 16, 530742520);
    b = hh(b, c, d, a, k[2], 23, -995338651);
    a = ii(a, b, c, d, k[0], 6, -198630844);
    d = ii(d, a, b, c, k[7], 10, 1126891415);
    c = ii(c, d, a, b, k[14], 15, -1416354905);
    b = ii(b, c, d, a, k[5], 21, -57434055);
    a = ii(a, b, c, d, k[12], 6, 1700485571);
    d = ii(d, a, b, c, k[3], 10, -1894986606);
    c = ii(c, d, a, b, k[10], 15, -1051523);
    b = ii(b, c, d, a, k[1], 21, -2054922799);
    a = ii(a, b, c, d, k[8], 6, 1873313359);
    d = ii(d, a, b, c, k[15], 10, -30611744);
    c = ii(c, d, a, b, k[6], 15, -1560198380);
    b = ii(b, c, d, a, k[13], 21, 1309151649);
    a = ii(a, b, c, d, k[4], 6, -145523070);
    d = ii(d, a, b, c, k[11], 10, -1120210379);
    c = ii(c, d, a, b, k[2], 15, 718787259);
    b = ii(b, c, d, a, k[9], 21, -343485551);
    x[0] = add32(a, x[0]);
    x[1] = add32(b, x[1]);
    x[2] = add32(c, x[2]);
    x[3] = add32(d, x[3]);
}
function cmn(q, a, b, x, s, t) {
    a = add32(add32(a, q), add32(x, t));
    return add32((a << s) | (a >>> (32 - s)), b);
}
function ff(a, b, c, d, x, s, t) {
    return cmn((b & c) | (~b & d), a, b, x, s, t);
}
function gg(a, b, c, d, x, s, t) {
    return cmn((b & d) | (c & ~d), a, b, x, s, t);
}
function hh(a, b, c, d, x, s, t) {
    return cmn(b ^ c ^ d, a, b, x, s, t);
}
function ii(a, b, c, d, x, s, t) {
    return cmn(c ^ (b | ~d), a, b, x, s, t);
}
function md51(s) {
    const n = s.length, state = [1732584193, -271733879, -1732584194, 271733878];
    let i;
    for (i = 64; i <= s.length; i += 64) {
        md5cycle(state, md5blk(s.substring(i - 64, i)));
    }
    s = s.substring(i - 64);
    const tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (i = 0; i < s.length; i++)
        tail[i >> 2] |= s.charCodeAt(i) << (i % 4 << 3);
    tail[i >> 2] |= 0x80 << (i % 4 << 3);
    if (i > 55) {
        md5cycle(state, tail);
        for (i = 0; i < 16; i++)
            tail[i] = 0;
    }
    tail[14] = n * 8;
    md5cycle(state, tail);
    return state;
}
function md5blk(s) {
    /* I figured global was faster.   */
    const md5blks = [];
    let i;
    for (i = 0; i < 64; i += 4) {
        md5blks[i >> 2] =
            s.charCodeAt(i) +
                (s.charCodeAt(i + 1) << 8) +
                (s.charCodeAt(i + 2) << 16) +
                (s.charCodeAt(i + 3) << 24);
    }
    return md5blks;
}
const HEX_CHR = '0123456789abcdef'.split('');
function rhex(n) {
    let s = '', j = 0;
    for (; j < 4; j++)
        s += HEX_CHR[(n >> (j * 8 + 4)) & 0x0f] + HEX_CHR[(n >> (j * 8)) & 0x0f];
    return s;
}
function hex(x) {
    for (let i = 0; i < x.length; i++)
        x[i] = rhex(x[i]);
    return x.join('');
}
let add32 = (a, b) => {
    return (a + b) & 0xffffffff;
};
/**
 * Generate an MD5 hash for an input string
 * @param {string} s input string
 * @returns {string} md5 hash
 */
function md5(s) {
    return hex(md51(s));
}
if (md5('hello') !== '5d41402abc4b2a76b9719d911017c592') {
    add32 = (x, y) => {
        const lsw = (x & 0xffff) + (y & 0xffff), msw = (x >> 16) + (y >> 16) + (lsw >> 16);
        return (msw << 16) | (lsw & 0xffff);
    };
}

function resolveMixpanelUserId(email) {
    return '@' + md5(email.toLowerCase()).toUpperCase();
}
function resolveMixpanelServerId(serverHostname) {
    return md5(serverHostname.toLowerCase()).toUpperCase();
}

var _FakeStorage_internalStorage;
function checkLocalStorageAvailability() {
    try {
        const testKey = '___localStorageAvailabilityTest';
        const storage = globalThis.localStorage;
        storage.setItem(testKey, testKey);
        storage.getItem(testKey);
        storage.removeItem(testKey);
        return true;
    }
    catch (e) {
        return false;
    }
}
/**
 * In memory implementation of the Storage interface, for use when LocalStorage
 * isn't available
 */
class FakeStorage {
    constructor() {
        _FakeStorage_internalStorage.set(this, new Map());
    }
    clear() {
        __classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").clear();
    }
    getItem(key) {
        return __classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").get(key) || null;
    }
    key(index) {
        return [...__classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").keys()][index] || null;
    }
    removeItem(key) {
        __classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").delete(key);
    }
    setItem(key, value) {
        __classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").set(key, value);
    }
    get length() {
        return __classPrivateFieldGet(this, _FakeStorage_internalStorage, "f").size;
    }
}
_FakeStorage_internalStorage = new WeakMap();
/**
 * Whether or not the local storage is available in this session
 */
const isLocalStorageAvailable = checkLocalStorageAvailability();
/**
 * Localstorage (real or faked) to use in this session
 */
const internalStorage = isLocalStorageAvailable
    ? globalThis.localStorage
    : new FakeStorage();
/**
 * Utility for nicer reads/writes from/to LocalStorage without having to worry about the browser
 * throwing a hissy fit because the page is opened in Incognito mode or node not having localStorage at all
 */
const SafeLocalStorage = {
    get(key) {
        return internalStorage.getItem(key);
    },
    set(key, value) {
        internalStorage.setItem(key, value);
    },
    remove(key) {
        internalStorage.removeItem(key);
    },
    /**
     * Flag for telling if we're using a real localStorage or faking it with a basic in-memory collection
     */
    isRealLocalStorage: isLocalStorageAvailable
};

var OperatingSystem;
(function (OperatingSystem) {
    OperatingSystem["Windows"] = "win";
    OperatingSystem["Mac"] = "mac";
    OperatingSystem["Linux"] = "linux";
    OperatingSystem["Android"] = "android";
    OperatingSystem["iOS"] = "ios";
    OperatingSystem["Other"] = "other";
})(OperatingSystem || (OperatingSystem = {}));
function resolveOsFromPlatform() {
    if (!globalThis || !globalThis.navigator || !('platform' in globalThis.navigator)) {
        return null;
    }
    const platform = (globalThis.navigator.platform || '').toLowerCase();
    if (platform.startsWith('mac'))
        return OperatingSystem.Mac;
    if (platform.startsWith('linux') ||
        platform.startsWith('freebsd') ||
        platform.startsWith('sunos'))
        return OperatingSystem.Linux;
    if (platform.startsWith('win'))
        return OperatingSystem.Windows;
    if (platform.startsWith('iphone') ||
        platform.startsWith('ipad') ||
        platform.startsWith('ipod'))
        return OperatingSystem.iOS;
    if (platform.startsWith('android'))
        return OperatingSystem.Android;
    return OperatingSystem.Other;
}
function resolveOsFromUserAgent() {
    if (!globalThis || !globalThis.navigator || !('userAgent' in globalThis.navigator)) {
        return null;
    }
    const userAgent = globalThis.navigator.userAgent;
    if (userAgent.includes('X11') || userAgent.includes('Linux'))
        return OperatingSystem.Linux;
    if (userAgent.includes('Win'))
        return OperatingSystem.Windows;
    if (userAgent.includes('Mac'))
        return OperatingSystem.Mac;
    if (userAgent.includes('Android'))
        return OperatingSystem.Android;
    if (userAgent.includes('iPhone') ||
        userAgent.includes('iPad') ||
        userAgent.includes('iPhone'))
        return OperatingSystem.iOS;
    return OperatingSystem.Other;
}
function getClientOperatingSystem() {
    if (!globalThis || !globalThis.navigator) {
        // Skipped in server-side
        return OperatingSystem.Other;
    }
    const osFromPlatform = resolveOsFromPlatform();
    // If Linux it could actually be Android, so let's check userAgent
    if (osFromPlatform &&
        ![OperatingSystem.Other, OperatingSystem.Linux].includes(osFromPlatform)) {
        return osFromPlatform;
    }
    const userAgentPlatform = resolveOsFromUserAgent();
    return userAgentPlatform || OperatingSystem.Other;
}
/**
 * Check if user is in Safari browser
 */
function isSafari() {
    if (!globalThis || !globalThis.navigator || !('userAgent' in globalThis.navigator)) {
        return false;
    }
    const userAgent = globalThis.navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
}
/**
 * Check if user is in Brave browser
 */
function isBrave() {
    if (!globalThis || !globalThis.navigator || !('brave' in globalThis.navigator)) {
        return false;
    }
    const braveObj = get(globalThis.navigator, 'brave');
    if (!braveObj)
        return false;
    return !!get(braveObj, 'isBrave', false);
}
const isBraveOrSafari = () => isBrave() || isSafari();

const shouldPolyfillIdleCallback = isSafari() || !globalThis.requestIdleCallback;
/**
 * requestIdleCallback w/ proper polyfills
 */
const requestIdleCallback = shouldPolyfillIdleCallback
    ? function (cb) {
        const start = Date.now();
        return setTimeout(function () {
            cb({
                didTimeout: false,
                timeRemaining() {
                    return Math.max(0, 50 - (Date.now() - start));
                }
            });
        }, 1); // Timer is actually a number at the end, just w/ extra bits on top of it
    }
    : globalThis.requestIdleCallback;
const cancelIdleCallback = shouldPolyfillIdleCallback ? clearTimeout : globalThis.cancelIdleCallback;

export { AllScopes, index$2 as Observability, OperatingSystem, index$3 as RichTextEditor, RoleInfo, Roles, SafeLocalStorage, Scopes, SourceAppNames, SourceApps, index as SpeckleViewer, TIME, TimeoutError, WaitIntervalUntilCanceledError, WebhookTriggers, batchAsyncOperations, buildManualPromise, cancelIdleCallback, ensureError, getClientOperatingSystem, isBrave, isBraveOrSafari, isNonNullable, isNullOrUndefined, isSafari, isUndefinedOrVoid, md5, profile, profileSync, requestIdleCallback, resolveMixpanelServerId, resolveMixpanelUserId, retry, timeoutAt, wait, waitIntervalUntil };
